import { Icon } from '@eventbrite/eds-icon';
import {
    ChevronDownChunky,
    ChevronUpChunky,
    GlobeChunky,
    MapPinFill,
} from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { EVENT_LOCATION_MAP_ID } from '../../../_shared/constants';
import {
    Detail,
    DetailContent,
    DetailHeading,
    DetailIcon,
    ICON_SIZE,
} from '../../../_shared/Detail/Detail';
import './Location.scss';

export type LocationProps = {
    hasAddress?: boolean;
    hasMap?: boolean;
    venueMultilineAddress?: string[];
    venueName?: string;
    isOnlineEvent: boolean;
    onlineEventUrl?: string;
};

export type MapToggleButtonProps = {
    onToggleButtonMap?: (isMapExpanded: boolean) => void;
    isMapExpanded?: boolean;
};

const ButtonContent: React.FC<Pick<MapToggleButtonProps, 'isMapExpanded'>> = ({
    isMapExpanded,
}) => {
    const HIDE_MAP_TEXT = gettext('Hide map');
    const SHOW_MAP_TEXT = gettext('Show map');
    const iconType = isMapExpanded ? (
        <ChevronUpChunky />
    ) : (
        <ChevronDownChunky />
    );
    const text = isMapExpanded ? HIDE_MAP_TEXT : SHOW_MAP_TEXT;

    return (
        <>
            {text}
            <Icon type={iconType} size="small" color="ui-blue--hover" />
        </>
    );
};

export const MapToggleButton = ({
    onToggleButtonMap,
    isMapExpanded,
}: MapToggleButtonProps) => {
    const _handleToggleMap = () => {
        if (onToggleButtonMap) {
            onToggleButtonMap(!isMapExpanded);
        }
    };

    return (
        <div className="map-button-toggle">
            <button
                className="eds-btn eds-btn--link"
                onClick={_handleToggleMap}
                aria-expanded={isMapExpanded}
                aria-controls={EVENT_LOCATION_MAP_ID}
            >
                <ButtonContent isMapExpanded={isMapExpanded} />
            </button>
        </div>
    );
};

export const Location = ({
    isOnlineEvent,
    onlineEventUrl,
    hasAddress,
    hasMap,
    venueMultilineAddress,
    venueName,
    onToggleButtonMap,
    isMapExpanded,
}: LocationProps & MapToggleButtonProps) => {
    const OnlineEvent = () => {
        return (
            <>
                {<p>{gettext('Online')}</p>}
                {onlineEventUrl && (
                    <a
                        className="eds-link"
                        href={onlineEventUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {gettext('Access link')}
                    </a>
                )}
            </>
        );
    };

    const InPersonEvent = () => {
        const address = venueMultilineAddress?.join(' ');

        return (
            <>
                <p>
                    {venueName && <strong>{venueName}</strong>}
                    {venueMultilineAddress && ` ${address}`}
                </p>
                {hasMap && (
                    <MapToggleButton
                        onToggleButtonMap={onToggleButtonMap}
                        isMapExpanded={isMapExpanded}
                    />
                )}
            </>
        );
    };

    const IconType = () => (
        <>{isOnlineEvent ? <GlobeChunky /> : <MapPinFill />}</>
    );

    const LABEL_ID = 'location-heading';

    return (
        <Detail ariaLabelledBy={LABEL_ID}>
            <DetailIcon>
                <Icon type={<IconType />} size={ICON_SIZE} />
            </DetailIcon>
            <DetailContent>
                <DetailHeading>
                    <h3 id={LABEL_ID}>{gettext('Location')}</h3>
                </DetailHeading>
                {isOnlineEvent && <OnlineEvent />}
                {hasAddress && <InPersonEvent />}
                {!hasAddress && !isOnlineEvent && gettext('To be announced')}
            </DetailContent>
        </Detail>
    );
};
